import type { FetchHandlerObject } from '@atcute/client';

import { createDPoPFetch } from './dpop';
import { OAuthServerAgent } from './server-agent';
import { type SessionGetOptions, deleteStoredSession, getSession } from './sessions';
import type { Did, Session } from './types';

/**
 * A `FetchHandlerObject` (compatible with `@atcute/client`'s `XRPC`) backed by
 * an OAuth session. It signs requests with DPoP, attaches the access token, and
 * transparently refreshes the session when the access token expires.
 */
export class OAuthUserAgent implements FetchHandlerObject {
	#fetch: typeof fetch;
	#getSessionPromise: Promise<Session> | undefined;

	constructor(public session: Session) {
		this.#fetch = createDPoPFetch(session.dpopKey, false);
	}

	get sub(): Did {
		return this.session.info.sub;
	}

	getSession(options?: SessionGetOptions): Promise<Session> {
		const promise = getSession(this.session.info.sub, options);

		promise
			.then((session) => {
				this.session = session;
			})
			.finally(() => {
				this.#getSessionPromise = undefined;
			});

		return (this.#getSessionPromise = promise);
	}

	async signOut(): Promise<void> {
		const sub = this.session.info.sub;

		try {
			const { dpopKey, info, token } = await getSession(sub, { allowStale: true });
			const server = new OAuthServerAgent(info.server, dpopKey);

			await server.revoke(token.refresh ?? token.access);
		} finally {
			deleteStoredSession(sub);
		}
	}

	async handle(pathname: string, init?: RequestInit): Promise<Response> {
		await this.#getSessionPromise;

		const headers = new Headers(init?.headers);

		let session = this.session;
		let url = new URL(pathname, session.info.aud);

		headers.set('authorization', `${session.token.type} ${session.token.access}`);

		let response = await this.#fetch(url, { ...init, headers });
		if (!isInvalidTokenResponse(response)) {
			return response;
		}

		try {
			if (this.#getSessionPromise) {
				session = await this.#getSessionPromise;
			} else {
				session = await this.getSession();
			}
		} catch {
			return response;
		}

		if (init?.body instanceof ReadableStream) {
			return response;
		}

		url = new URL(pathname, session.info.aud);
		headers.set('authorization', `${session.token.type} ${session.token.access}`);

		return await this.#fetch(url, { ...init, headers });
	}
}

const isInvalidTokenResponse = (response: Response) => {
	if (response.status !== 401) {
		return false;
	}

	const auth = response.headers.get('www-authenticate');

	return (
		auth != null &&
		(auth.startsWith('Bearer ') || auth.startsWith('DPoP ')) &&
		auth.includes('error="invalid_token"')
	);
};
